import { useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Divider,
  Stack,
  useMediaQuery,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Block as BlockIcon,
  ArrowBack as ArrowBackIcon,
  CheckCircle as CheckCircleIcon,
  History as HistoryIcon,
} from "@mui/icons-material";
import { useAuth } from "../../../../../auth";
import { StockingAreaWtihAllPropertiesDto } from "../../../../../api/bpm/model";
import {
  getFindStockingAreaQueryOptions,
  getFindStockingAreaQueryKey,
  getEnableStockingAreaMutationOptions,
  getDisableStockingAreaMutationOptions,
  getDeleteStockingAreaMutationOptions,
} from "../../../../../api/bpm/endpoints";
import { ConfirmModal } from "../../../../../components/ConfirmModal";
import { LogsModal } from "../../../../../components/LogsModal";
import { useQueryClient, useQuery, useMutation } from "@tanstack/react-query";
import theme from "../../../../../theme";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/__auth/admin/locations/$locationId/")({
  component: LocationViewPage,
  staticData: {
    title: "routes.viewLocation",
  },
});

function LocationViewPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { locationId } = Route.useParams();
  const tenant = user?.tenant || "default-domain";
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [isLogsModalOpen, setIsLogsModalOpen] = useState(false);

  const {
    data,
    isLoading,
    error,
  } = useQuery({
    ...getFindStockingAreaQueryOptions(tenant, Number(locationId)),
  });

  const location = data as StockingAreaWtihAllPropertiesDto;

  const enableMutation = useMutation({
    ...getEnableStockingAreaMutationOptions(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: getFindStockingAreaQueryKey(tenant, Number(locationId)),
      });
      queryClient.invalidateQueries({
        queryKey: ["listStockingarea"],
      });
      setIsConfirmOpen(false);
    },
  });

  const disableMutation = useMutation({
    ...getDisableStockingAreaMutationOptions(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: getFindStockingAreaQueryKey(tenant, Number(locationId)),
      });
      queryClient.invalidateQueries({
        queryKey: ["listStockingarea"],
      });
      setIsConfirmOpen(false);
    },
  });

  const deleteMutation = useMutation({
    ...getDeleteStockingAreaMutationOptions(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["listStockingarea"],
      });
      navigate({ to: "/admin/locations", search: { page: 1, size: 10 } });
    },
  });

  const handleToggleStatus = () => {
    if (location?.enabled) {
      disableMutation.mutate({
        stockingAreaId: Number(locationId),
        tenant,
      });
    } else {
      enableMutation.mutate({
        stockingAreaId: Number(locationId),
        tenant,
      });
    }
  };

  const handleDelete = () => {
    deleteMutation.mutate({
      stockingAreaId: Number(locationId), tenant,
    });
  };

  const isMutationLoading =
    enableMutation.isPending ||
    disableMutation.isPending ||
    deleteMutation.isPending;

  if (isLoading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {t("admin.locations.loadError")}
        </Alert>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() =>
            navigate({ to: "/admin/locations", search: { page: 1, size: 10 } })
          }
        >{t("admin.common.backToList")}</Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ display: "flex", alignItems: "center", mb: 4 }}>
        <Box
          sx={{
            width: 4,
            height: 40,
            bgcolor: "primary.main",
            borderRadius: 1,
            mr: 2,
          }}
        />
        <Typography variant="h5" fontWeight={600}>
          {t("admin.locations.viewData")} {location?.name}
        </Typography>
      </Box>

      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>{t("admin.locations.sectionData")}</Typography>

      <Stack spacing={2.5} sx={{ mb: 4 }}>
        <TextField
          label="Id"
          value={location?.id || ""}
          fullWidth
          size="small"
          slotProps={{ input: { readOnly: true } }}
        />
        <TextField
          label={t("common.name")}
          value={location?.name || ""}
          fullWidth
          size="small"
          slotProps={{ input: { readOnly: true } }}
        />
        <TextField
          label={t("admin.common.description")}
          value={location?.description || ""}
          fullWidth
          size="small"
          multiline
          rows={3}
          slotProps={{ input: { readOnly: true } }}
        />
      </Stack>

      <Divider sx={{ mb: 3 }} />

      <Box
        sx={
          isMobile
            ? { display: "flex", flexDirection: "column", gap: 1.5 }
            : { display: "flex", justifyContent: "flex-end", gap: 1.5 }
        }
      >
        <Button
          variant="contained"
          color={location?.enabled ? "error" : "success"}
          startIcon={location?.enabled ? <BlockIcon /> : <CheckCircleIcon />}
          onClick={() => setIsConfirmOpen(true)}
          disabled={isMutationLoading}
        >
          {location?.enabled ? t("common.deactivate").toUpperCase() : t("common.activate").toUpperCase()}
        </Button>
        <Button
          variant="contained"
          color="error"
          startIcon={<DeleteIcon />}
          onClick={() => setIsDeleteConfirmOpen(true)}
          disabled={isMutationLoading}
        >{t("common.delete").toUpperCase()}</Button>
        <Button
          variant="contained"
          color="primary"
          startIcon={<EditIcon />}
          onClick={() =>
            navigate({ to: `/admin/locations/${locationId}/edit` })
          }
        >{t("common.edit").toUpperCase()}</Button>
        <Button
          variant="outlined"
          startIcon={<HistoryIcon />}
          onClick={() => setIsLogsModalOpen(true)}
        >
          LOG
        </Button>
        <Button
          variant="outlined"
          onClick={() =>
            navigate({ to: "/admin/locations", search: { page: 1, size: 10 } })
          }
        >{t("common.cancel").toUpperCase()}</Button>
      </Box>

      <ConfirmModal
        open={isConfirmOpen}
        onClose={() => setIsConfirmOpen(false)}
        onConfirm={handleToggleStatus}
        title={
          location?.enabled
            ? t("admin.locations.deactivateTitle")
            : t("admin.locations.activateTitle")
        }
        description={
          location?.enabled
            ? t("admin.locations.deactivateConfirmation", { name: location?.name })
            : t("admin.locations.activateConfirmation", { name: location?.name })
        }
        confirmText={location?.enabled ? t("common.deactivate") : t("common.activate")}
        confirmColor={location?.enabled ? "error" : "primary"}
        isLoading={isMutationLoading}
      />

      <ConfirmModal
        open={isDeleteConfirmOpen}
        onClose={() => setIsDeleteConfirmOpen(false)}
        onConfirm={handleDelete}
        title={t("admin.locations.deleteTitle")}
        description={t("admin.locations.deleteConfirmation", { name: location?.name })}
        confirmText={t("common.delete")}
        confirmColor="error"
        isLoading={isMutationLoading}
      />

      <LogsModal
        open={isLogsModalOpen}
        onClose={() => setIsLogsModalOpen(false)}
        entityId={locationId}
        entityName={location?.name || ""}
        tenant={tenant}
        entityType="stocking-areas"
      />
    </Container>
  );
}
